import writeXlsxFile from "write-excel-file";
import moment from "moment-timezone";
import { ResourceCentre } from "../interfaces/ResourceCentreInterface";
import { convertADtoBS } from "../components/Calendar/functions/calendarFunctions";

enum extensions {
  csv = "csv",
  pdf = "pdf",
  xlsx = "xlsx",
  xml = "xml"
}

const types = {
  csv: "text/csv",
  pdf: "application/pdf",
  xls: "application/vnd.ms-excel",
  xml: "text/xml"
};

export const download = (content: string, name: string, type: extensions): void => {
  const link = document.createElement("a");
  document.body.appendChild(link);
  if (typeof content === "object") {
    // eslint-disable-next-line no-param-reassign
    content = URL.createObjectURL(content);
    link.href = content;
    link.download = `${name}.${extensions[type]}`;
    link.click();
    window.URL.revokeObjectURL(content);
    return;
  }

  link.setAttribute(
    "href",
    `data:${types[type]};charset=utf-8,\uFEFF${encodeURIComponent(content)}`
  );
  link.setAttribute("download", `${name}.${extensions[type]}`);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
  }, 1000);
};

const centerText = (text: string, columns: any[]): string => {
  const totalCols = columns.length;
  const leftPad = Math.floor(totalCols / 2);
  return `${new Array(leftPad).fill("").join(",")},"${text}"`;
};
export const downloadCSV = (
  fileName: string,
  rows: any[],
  columns: any[],
  rowProcessor: (row) => any[],
  rc?: ResourceCentre,
  date?: { from: Date; until: Date }
): void => {
  const extraHeaders: string[] = [];

  if (rc?.name) {
    extraHeaders.push(
      centerText(
        `${rc.name}${
          rc.settings?.billingSettings?.panNo ? ` (Pan: ${rc.settings.billingSettings.panNo})` : ""
        }`,
        columns
      )
    );
  }
  if (date?.from && date?.until) {
    extraHeaders.push(
      centerText(
        `${convertADtoBS(moment(date.from)).formatted} - ${
          convertADtoBS(moment(date.until)).formatted
        }`,
        columns
      )
    );
  }

  // Join with newlines — blank line before the actual column headers
  const extraHeaderBlock = extraHeaders.length ? `${extraHeaders.join("\n")}\n\n` : "";

  const header = `${columns.join(",")}\n`;

  const content =
    extraHeaderBlock +
    header +
    rows.reduce((aggr, row) => {
      const processedRow = rowProcessor(row);
      return aggr.concat(
        processedRow ? `${processedRow.map((d) => (d ? `"${d}"` : "")).join(",")}\n` : ""
      );
    }, "");

  download(content, fileName, extensions.csv);
};

export const downloadExcel = (
  fileName: string,
  headerRow: { value: string }[],
  dataRow: any[],
  rc?: ResourceCentre,
  date?: { from: Date; until: Date }
): Promise<void> => {
  const extraRows: {
    value: string;
    span?: number;
    fontWeight?: string;
    align?: string;
    fontSize?: number;
  }[][] = [];

  if (rc?.name) {
    extraRows.push([]);
    extraRows.push([]);
    extraRows.push([
      {
        value: `${rc.name} ${
          rc.settings?.billingSettings?.panNo ? `(Pan: ${rc.settings.billingSettings.panNo})` : ""
        }`,
        span: headerRow.length,
        fontWeight: "bold",
        align: "center",
        fontSize: 12
      }
    ]);
  }

  if (rc?.address) {
    extraRows.push([
      {
        value: rc.address,
        span: headerRow.length,
        align: "center",
        fontSize: 12
      }
    ]);
  }

  extraRows.push([
    {
      value: fileName,
      span: headerRow.length,
      align: "center",
      fontSize: 16
    }
  ]);

  if (date?.from && date?.until) {
    extraRows.push([
      {
        value: `${convertADtoBS(moment(date.from)).formatted} - ${
          convertADtoBS(moment(date.until)).formatted
        }`,
        span: headerRow.length,
        align: "center",
        fontSize: 12
      }
    ]);
  }

  if (extraRows.length > 0) {
    extraRows.push([]);
    extraRows.push([]);
  }

  return writeXlsxFile([...extraRows, headerRow].concat(dataRow), {
    fileName: `${fileName}.${extensions.xlsx}`
  });
};

export const downloadXML = (fileName: string, data: string): void => {
  download(data, fileName, extensions.xml);
};

export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
export const PDF_EXTENSIONS = ["pdf"];

export const isImage = (fileName: string): boolean => {
  const extension = fileName?.split(".").pop()?.toLowerCase();
  return !!extension && IMAGE_EXTENSIONS.includes(extension);
};

export const isPDF = (fileName: string): boolean => {
  const extension = fileName?.split(".").pop()?.toLowerCase();
  return !!extension && PDF_EXTENSIONS.includes(extension);
};
